import React, {
	memo,
	useCallback,
	useEffect,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	DragDropContext,
	Draggable,
	Droppable
} from 'react-beautiful-dnd';
import {
	Icon,
	Input
} from 'semantic-ui-react';

import { useProgramState } from '../../../../../../contexts/ProgramsContext';

import TemplateModuleInfoPopup from './TemplateModuleInfoPopup';
import TemplateModulePreviewModal from './TemplateModulePreviewModal';
import TemplateModuleFilterModal from './TemplateModuleFilterModal';

import styles from '../../programs.module.css';

const TemplateLayout = ( {
	error,
	formData,
	updateField
} ) => {
	const { templateModules, templateModuleFilters } = useProgramState();
	const [ availableModules, setAvailableModules ] = useState( templateModules );
	const [ preview, setPreview ] = useState( '' );
	const [ templateModuleFilterModal, setTemplateModuleFilterModal ] = useState( false );
	const [ search, setSearch ] = useState( '' );

	const closeTemplateModulePreviewModal = useCallback( () => {
		setPreview( '' );
	}, [] );

	const closeTemplateModuleFilterModal = useCallback( () => {
		setTemplateModuleFilterModal( false );
	}, [] );

	const openTemplateModuleFilterModal = useCallback( () => {
		setTemplateModuleFilterModal( true );
	}, [] );

	const handleSearch = useCallback( ( e, { value } ) => {
		setSearch( value );
	}, [] );

	const [ lists, setLists ] = useState( {
		available: [],
		included: []
	} );

	useEffect( () => {
		const availableModules = templateModules.filter( module => (
			templateModuleFilters.length
				? module.labels.some( ( { id } ) => templateModuleFilters.find( filter => filter.id === id ) ) // labels on backend
				: true
		)
			&& (
				search.length
					? module.name.toLowerCase().includes( search.toLowerCase() )
					: true
			) );

		setAvailableModules( availableModules );
	}, [
		templateModuleFilters,
		templateModules,
		search
	] );

	useEffect( () => {
		const included = [];
		let available = availableModules;

		if ( formData?.layout ) {
			formData.layout.forEach( id => {
				const module = templateModules.find( module => module.id === id );

				if ( module ) {
					included.push( module );
					available = available.filter( module => module.id !== id );
				}
			} );
		}

		setLists( { included, available } );
	}, [
		availableModules,
		formData,
		templateModules
	] );

	const reorder = useCallback( (
		list,
		startIndex,
		endIndex
	) => {
		const result = Array.from( list );
		const [ removed ] = result.splice( startIndex, 1 );

		result.splice( endIndex, 0, removed );

		return result;
	}, [] );

	const move = useCallback( ( {
		source: {
			droppableId: sourceId,
			index: sourceIndex
		},
		destination: {
			droppableId: destinationId,
			index: destinationIndex
		}
	} ) => {
		const sourceClone = [ ...lists[ sourceId ] ];
		const destClone = [ ...lists[ destinationId ] ];
		const [ removed ] = sourceClone.splice( sourceIndex, 1 );

		destClone.splice(
			destinationIndex,
			0,
			removed
		);

		const result = {};

		result[ sourceId ] = sourceClone;
		result[ destinationId ] = destClone;

		return result;
	}, [ lists ] );

	const onDragEnd = useCallback( ( {
		source,
		destination
	} ) => {
		// dropped outside the list
		if ( !destination ) {
			return;
		}

		if ( source.droppableId === destination.droppableId ) {
			// dropped in the same list
			const items = reorder(
				lists[ source.droppableId ],
				source.index,
				destination.index
			);

			if ( destination.droppableId === 'included' ) {
				const value = items.map( item => item.id );

				updateField( {
					target: {
						name: 'layout',
						value
					}
				} );
			}

			setLists( prevState => ( {
				...prevState,
				[ source.droppableId ]: items
			} ) );
		} else {
			// dropped in a different list
			const result = move( {
				source,
				destination
			} );

			const value = result.included.map( item => item.id );

			updateField( { target: { name: 'layout', value } } );

			setLists( prevState => ( {
				...prevState,
				...result
			} ) );
		}
	}, [
		lists,
		move,
		reorder,
		updateField
	] );

	const getItemStyle = useCallback( ( isDragging, draggableStyle ) => ( {
		// some basic styles to make the items look a bit nicer
		display: 'flex',
		justifyContent: 'space-between',
		userSelect: 'none',
		padding: '1rem',
		border: '1px solid rgba(34, 36, 38, 0.15)',
		margin: '8px 0',

		// change background colour if dragging
		background: isDragging
			? '#fff'
			: '#fff',
		boxShadow: isDragging
			? '1px 1px 5px rgba(0,0,0,0.2)'
			: 'none',
		// outline: isDragging
		//  ? '1px solid var( --main )'
		//  : 'none',

		// styles we need to apply on draggables
		...draggableStyle,
		transition: !isDragging
			? 'translate(0,0)'
			: ''
	} ), [] );

	const getListStyle = useCallback( isDraggingOver => ( {
		height:	`${ availableModules
			? ( 52.73 * 10 ) + 16
			: 52.73 + 16 }px`,
		padding: '8px 1rem',
		borderTop: '1px solid rgba(34, 36, 38, 0.15)',
		width: 360,
		background: isDraggingOver
			? 'var( --mainTranslucent )'
			: null,
		overflowY: 'auto'
	} ), [ availableModules ] );

	const mapModules = useCallback( list => list.map( ( {
		id,
		name,
		description,
		photoUri
	}, index ) => {
		const showPreview = () => setPreview( photoUri );

		return (
			<Draggable
				key={ id.toString() }
				draggableId={ id.toString() }
				index={ index }
			>
				{ ( provided, snapshot ) => (
					<div
						ref={ provided.innerRef }
						{ ...provided.draggableProps }
						{ ...provided.dragHandleProps }

						style={
							getItemStyle(
								snapshot.isDragging,
								provided.draggableProps.style
							)
						}
					>
						{ name }
						<div>
							{ photoUri
								?	<Icon
									link
									name="eye"
									onClick={ showPreview }
									size="large"
								/>
								: null
							}
							{ description
								? <TemplateModuleInfoPopup description={ description } />
								: null
							}
						</div>
					</div>
				) }
			</Draggable>
		);
	} ), [ getItemStyle ] );

	return (
		<div className={ styles.templateLayoutContainer }>
			<DragDropContext onDragEnd={ onDragEnd }>
				<div className={ styles.listContainer }>
					<div className={ styles.listHeader }>
						<h4>Available Modules</h4>
						<Icon
							link
							name="filter"
							onClick={ openTemplateModuleFilterModal }
							style={ { cursor: 'pointer' } }
							title="Filter Template Modules By Tag"
						/>
					</div>
					<Input
						className={ styles.moduleSearch }
						fluid
						onChange={ handleSearch }
						icon="search"
						size="small"
						value={ search }
					/>
					<Droppable droppableId="available">
						{ ( provided, snapshot ) => (
							<div
								ref={ provided.innerRef }
								style={ getListStyle( snapshot.isDraggingOver ) }
							>
								{
									!lists.available.length && !snapshot.isDraggingOver
										? <small className={ styles.emptyListMessage }>All available modules have been included in the template.</small>
										: mapModules( lists.available )
								}
								{ provided.placeholder }
							</div>
						) }
					</Droppable>
				</div>

				<div className={ `${ styles.listContainer } ${ error && styles.error }` }>
					<div className={ styles.listHeader }>
						<h4>Included Modules</h4>
					</div>
					<Droppable droppableId="included">
						{ ( provided, snapshot ) => (
							<div
								ref={ provided.innerRef }
								style={ getListStyle( snapshot.isDraggingOver ) }
							>
								{ !lists.included.length && !snapshot.isDraggingOver
									? <small className={ styles.emptyListMessage }>Drag modules from the list of available modules to include them in the template.</small>
									: mapModules( lists.included )
								}
								{ provided.placeholder }
							</div>
						)}
					</Droppable>
				</div>
			</DragDropContext>
			<TemplateModulePreviewModal
				onClose={ closeTemplateModulePreviewModal }
				photoUri={ preview }
			/>
			<TemplateModuleFilterModal
				open={ templateModuleFilterModal }
				closeModal={ closeTemplateModuleFilterModal }
				filters={ [] }
			/>
		</div>
	);
};

TemplateLayout.propTypes = {
	error: PropTypes.bool,
	formData: PropTypes.object.isRequired,
	updateField: PropTypes.func.isRequired
};

export default memo( TemplateLayout );
